<template>
  <ApolloQuery
    :key="useLegacyBookListQuery ? 'book-list-legacy' : 'book-list-current'"
    :query="(gql) => bookListQuery"
    :variables="{ id: bookListId }"
    :update="updateBookList"
  >
    <template #default="{ result: { error, data }, isLoading }">
      <success-snackbar
        v-model="snackbar"
        :message="$t('bookList.removedFromList')"
      />
      <span
        v-if="handleBookListQueryError(error)"
        class="d-none"
      />
      <v-dialog
        v-model="duplicateDialog"
        max-width="520px"
      >
        <v-card>
          <v-card-title class="text-h6">
            {{ $t('bookList.duplicateBookList') }}
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="duplicateName"
              :label="$t('bookList.listName')"
              outlined
              dense
            />
            <v-switch
              v-if="canEditCuration"
              v-model="duplicateCopyClientAssociation"
              :label="$t('bookList.copyClientAssociation')"
              dense
            />
            <v-switch
              v-if="canEditCuration"
              v-model="duplicateCopyPricingSettings"
              :label="$t('bookList.copyPricingSettings')"
              dense
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="duplicateDialog = false"
            >
              {{ $t('button.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="duplicatingList"
              :disabled="duplicatingList || !duplicateName.trim()"
              @click="submitDuplicateList"
            >
              {{ $t('button.apply') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-container>
        <header class="quote-header mb-6">
          <div class="d-flex flex-column flex-md-row align-md-end">
            <div class="flex-grow-1">
              <div class="text-overline primary--text mb-1">
                {{ $t('routes.userBookList') }}
              </div>
              <v-skeleton-loader
                v-if="isLoading"
                type="heading"
                width="400"
                class="mb-2"
              />
              <h1
                v-else
                class="text-h4 text-md-h3 font-weight-bold mb-2 quote-title"
              >
                {{ data?.name }}
              </h1>
              <div
                v-if="data"
                class="d-flex align-center flex-wrap text-caption tertiaryText--text"
              >
                <span>{{ data.bookListItems ? data.bookListItems.length : 0 }} {{ $t('bookList.totalTitles').toLowerCase() }}</span>
                <span class="quote-meta-dot">·</span>
                <span>{{ totalQuantity(data) }} {{ $t('bookList.totalQuantity').toLowerCase() }}</span>
                <template v-if="data.discountPercentage > 0">
                  <span class="quote-meta-dot">·</span>
                  <v-chip
                    x-small
                    color="primary"
                    class="font-weight-bold"
                  >
                    {{ $t('bookList.percentOff', { percent: data.discountPercentage }) }}
                  </v-chip>
                </template>
              </div>
            </div>
            <add-book-list-to-cart-button
              :book-list="data?.bookListItems?.map((item) => item.productId)"
              :book-list-id="data?.id"
              :before-mutate="() => flushPendingQuantities(data)"
              :open-cart-on-done="false"
              class="mt-4 mt-md-0 ml-md-6 flex-shrink-0"
            />
          </div>

          <div
            v-if="data"
            class="d-flex my-4"
          >
            <share-list-buttons
              :is-owner="canMutateList(data)"
              :book-list-id="$route.params.id"
              :is-public="data?.isPublic ?? false"
              :allow-public-cart="data?.allowPublicCart ?? false"
              :public-cart-supported="!useLegacyBookListQuery"
              :discount-percentage="data?.discountPercentage"
              :book-list-name="data?.name"
              :book-list-slug="data?.slug"
            />
          </div>
          <v-row
            no-gutters
            class="align-center"
          >
            <v-col
              cols="auto"
              class="d-flex flex-wrap align-center"
            >
              <v-btn
                v-if="canMutateList(data)"
                small
                :outlined="!editingList"
                :color="editingList ? 'primary' : undefined"
                elevation="0"
                class="mr-2 toolbar-button"
                @click="toggleEditing(data)"
              >
                <v-icon
                  small
                  left
                >
                  {{ editingList ? 'mdi-check' : 'mdi-pencil-outline' }}
                </v-icon>
                {{ !editingList ? $t("bookList.editList") : $t("bookList.stopEditing") }}
              </v-btn>
              <v-btn
                v-if="canMutateList(data)"
                small
                outlined
                elevation="0"
                class="mr-2 toolbar-button"
                :loading="duplicatingList"
                @click="openDuplicateDialog(data)"
              >
                <v-icon
                  small
                  left
                >
                  mdi-content-duplicate
                </v-icon>
                {{ $t("bookList.duplicate") }}
              </v-btn>
              <v-btn
                v-if="data?.bookListItems?.length && canManageList(data)"
                small
                outlined
                elevation="0"
                class="toolbar-button"
                :loading="exportIsLoading"
                @click="downloadBookListExport(data)"
              >
                <v-icon
                  small
                  left
                >
                  mdi-download-outline
                </v-icon>
                {{ $t("bookList.downloadExport") }}
              </v-btn>
            </v-col>
          </v-row>

          <div class="quote-rule mt-5" />
        </header>

        <!-- Settings Form (when editingList is true) -->
        <v-card
          v-if="editingList && canMutateList(data)"
          class="mb-6 pa-5 rounded-lg editing-card"
          outlined
        >
          <div class="text-overline tertiaryText--text">
            {{ $t('bookList.listSettings') }}
          </div>

          <div class="settings-section-label">
            {{ $t('details') }}
          </div>
          <v-row dense>
            <v-col
              cols="12"
              md="6"
            >
              <label
                class="input-label d-block"
                for="settings-list-name"
              >{{ $t('bookList.listName') }}</label>
              <v-text-field
                id="settings-list-name"
                v-model="editName"
                :placeholder="$t('bookList.listName')"
                class="input-field"
                solo
                flat
                dense
                tile
                hide-details
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <label class="input-label d-block">{{ $t('admin.bookLists.visibility') }}</label>
              <div class="d-flex align-center settings-switch-row">
                <v-switch
                  v-model="editIsPublic"
                  :label="$t('bookList.publicShareable')"
                  dense
                  class="mt-0 pt-0"
                />
              </div>
            </v-col>
            <v-col
              cols="12"
              class="mt-2"
            >
              <label
                class="input-label d-block"
                for="settings-notes"
              >{{ $t('bookList.notes') }}</label>
              <v-textarea
                id="settings-notes"
                v-model="editNotes"
                :placeholder="$t('bookList.notes')"
                class="input-field input-field--multiline"
                solo
                flat
                dense
                tile
                hide-details
                rows="2"
                auto-grow
              />
            </v-col>
          </v-row>

          <template v-if="canEditCuration">
            <div class="settings-section-label">
              {{ $t('bookList.pricing') }}
            </div>
            <v-row dense>
              <v-col
                cols="12"
                md="6"
              >
                <label
                  class="input-label d-block"
                  for="settings-discount"
                >{{ $t('bookList.discountPercentage') }}</label>
                <v-text-field
                  id="settings-discount"
                  v-model="editDiscountPercentage"
                  type="number"
                  suffix="%"
                  placeholder="0"
                  class="input-field"
                  solo
                  flat
                  dense
                  tile
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <TaxRegionSelect
                  v-model="editTaxRateId"
                />
              </v-col>
            </v-row>

            <div class="settings-section-label">
              {{ $t('bookList.audience') }}
            </div>
            <v-row dense>
              <v-col
                cols="12"
                md="6"
              >
                <EkitabuSchoolSelect
                  v-model="editSchoolId"
                  compact
                  clearable
                  :required="false"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <B2bCustomerSelect
                  v-model="editB2bCustomerId"
                />
              </v-col>
            </v-row>
          </template>
          <v-divider class="mt-1 mb-4" />
          <div class="d-flex">
            <v-spacer />
            <v-btn
              color="primary"
              elevation="0"
              :loading="savingSettings"
              :disabled="!editName || savingSettings"
              @click="saveSettings(data)"
            >
              <v-icon
                small
                left
              >
                mdi-check
              </v-icon>
              {{ $t('common.save') }}
            </v-btn>
          </div>
        </v-card>

        <!-- Add Books (when editing) -->
        <v-card
          v-if="editingList && canMutateList(data)"
          class="mb-6 pa-5 rounded-lg editing-card"
          outlined
        >
          <div class="text-overline tertiaryText--text mb-3">
            {{ $t('bookList.addBooks') }}
          </div>
          <v-text-field
            v-model="addSearchTerm"
            :label="$t('bookList.searchToAddPlaceholder')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            clearable
            :loading="addSearchLoading"
            @input="queueCatalogSearch"
            @click:clear="clearCatalogSearch"
          />
          <v-list
            v-if="addSearchResults.length"
            class="py-0"
          >
            <v-list-item
              v-for="book in addSearchResults"
              :key="book._id"
            >
              <v-list-item-avatar tile>
                <v-img
                  :src="book.thumbnailUrl || book.imageUrl"
                  :alt="book.title"
                />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ book.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  v-if="isAlreadyInList(book._id, data)"
                  small
                  text
                  disabled
                >
                  {{ $t('bookList.alreadyAdded') }}
                </v-btn>
                <v-btn
                  v-else
                  small
                  color="primary"
                  :loading="addingItemId === book._id"
                  :disabled="addingItemId !== null"
                  @click="addItemToList(book, data)"
                >
                  {{ $t('bookList.addToList') }}
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <p
            v-else-if="addSearchTerm && addSearchTerm.trim().length >= 2 && !addSearchLoading"
            class="text-body-2 grey--text mb-0 mt-2"
          >
            {{ $t('bookList.noSearchResults') }}
          </p>
        </v-card>

        <!-- Settings Summary (when editingList is false and metadata exists) -->
        <v-card
          v-else-if="data && !editingList && (showCurationSummary(data) || showNotesSummary(data))"
          class="mb-6 rounded-lg"
          outlined
        >
          <v-row
            v-if="showCurationSummary(data)"
            no-gutters
            class="stat-strip"
          >
            <v-col
              v-if="data.forSchool"
              cols="6"
              md="3"
              class="pa-4"
            >
              <div class="text-caption tertiaryText--text">
                {{ $t('bookList.school') }}
              </div>
              <div class="text-subtitle-2 font-weight-bold">
                {{ data.forSchool.name }}
              </div>
            </v-col>
            <v-col
              v-if="data.forB2bCustomer"
              cols="6"
              md="3"
              class="pa-4"
            >
              <div class="text-caption tertiaryText--text">
                {{ $t('bookList.b2bCustomer') }}
              </div>
              <div class="text-subtitle-2 font-weight-bold">
                {{ data.forB2bCustomer.organizationName }}
              </div>
            </v-col>
            <v-col
              v-if="data.taxRate"
              cols="6"
              md="3"
              class="pa-4"
            >
              <div class="text-caption tertiaryText--text">
                {{ $t('bookList.taxRate') }}
              </div>
              <div class="text-subtitle-2 font-weight-bold">
                {{ (data.taxRate.rate * 100).toFixed(0) }}%
              </div>
            </v-col>
            <v-col
              v-if="data.discountPercentage != null"
              cols="6"
              md="3"
              class="pa-4"
            >
              <div class="text-caption tertiaryText--text">
                {{ $t('bookList.discountPercentage') }}
              </div>
              <div class="text-subtitle-2 font-weight-bold success--text">
                {{ data.discountPercentage }}%
              </div>
            </v-col>
          </v-row>
          <template v-if="showNotesSummary(data)">
            <v-divider v-if="showCurationSummary(data)" />
            <div class="pa-4">
              <div class="text-caption tertiaryText--text">
                {{ $t('bookList.notes') }}
              </div>
              <div class="text-body-2">
                {{ data.notes }}
              </div>
            </div>
          </template>
        </v-card>

        <div
          v-if="data?.bookListItems?.length"
          class="d-flex align-center flex-wrap mb-4"
        >
          <div class="d-flex align-center flex-wrap flex-grow-1">
            <v-chip
              small
              :outlined="selectedCategoryFilters.length > 0"
              :color="!selectedCategoryFilters.length ? 'primary' : undefined"
              class="ma-1"
              @click="selectedCategoryFilters = []"
            >
              {{ $t('common.all') }}
            </v-chip>
            <v-chip-group
              v-model="selectedCategoryFilters"
              multiple
              column
              active-class="primary--text"
              class="category-chips"
            >
              <v-chip
                v-for="option in categoryFilterOptions"
                :key="option.value"
                :value="option.value"
                small
                outlined
                filter
              >
                {{ option.text }}
              </v-chip>
            </v-chip-group>
          </div>
          <span class="text-caption tertiaryText--text mx-4 text-no-wrap">
            {{ $t('bookList.filteredTitleCount', { shown: displayedBookListItems(data).length, total: data.bookListItems.length }) }}
          </span>
          <sort-menu-button
            v-model="sortField"
            :options="sortFieldOptions"
            :ascending.sync="orderAscending"
          />
        </div>

        <apollo-error-alert
          v-if="error"
          class="mt-3"
          :error="error"
        />
        <v-row
          v-else-if="isLoading"
        >
          <v-col
            v-for="n in loadingItemsCount"
            :key="n"
            class="col-4 col-sm-3 col-md-2 pa-1 pa-sm-2 pa-md-3"
          >
            <v-skeleton-loader
              type="card"
            />
          </v-col>
        </v-row>
        <v-row
          v-else
        >
          <book-list-item
            v-for="book in displayedBookListItems(data)"
            :id="'/books/'+book.productId"
            :key="book.productId"
            :book="book"
            :price="Number(book.basePrice)"
            :thumbnail-url="book.thumbnailUrl"
            :title="book.name"
            :currency="book.baseCurrency"
            :format="getFormat(book.productId)"
            :vat-included="getVatIncluded(book.productId)"
            use-external-ownership
            :external-owned-book="checkInBookshelf('/books/'+book.productId)"
            :external-bookshelf-loading="bookshelfLoading"
            :external-refetch-bookshelf="refetchBookshelf"
            :show-default-cart-actions="Number(book.basePrice) <= 0"
          >
            <!-- Quantity controls in slot -->
            <div
              class="mt-2 booklist-card-qty"
              @click.stop
            >
              <div class="d-flex align-center justify-space-between">
                <span class="text-caption font-weight-bold">{{ $t('bookList.quantity') }}</span>
                <div
                  v-if="editingList && canMutateList(data)"
                  class="d-flex align-center"
                >
                  <v-btn
                    icon
                    x-small
                    outlined
                    :aria-label="$t('checkout.decreaseQuantity')"
                    :disabled="displayQuantity(book) <= 1"
                    @click.stop="bumpQuantity(book, -1)"
                  >
                    <v-icon x-small>
                      mdi-minus
                    </v-icon>
                  </v-btn>
                  <span class="booklist-card-qty__count text-caption font-weight-bold mx-2">{{ displayQuantity(book) }}</span>
                  <v-btn
                    icon
                    x-small
                    outlined
                    :aria-label="$t('checkout.increaseQuantity')"
                    @click.stop="bumpQuantity(book, 1)"
                  >
                    <v-icon x-small>
                      mdi-plus
                    </v-icon>
                  </v-btn>
                </div>
                <span
                  v-else
                  class="booklist-card-qty__count text-caption font-weight-bold"
                >×{{ displayQuantity(book) }}</span>
              </div>
              <book-list-cart-quantity-control
                v-if="!(editingList && canMutateList(data)) && Number(book.basePrice) > 0"
                class="mt-2 booklist-card-qty__control"
                :product-id="book.productId"
                :book-id="'/books/'+book.productId"
                :book-list-id="data?.id"
                :book="cartBookFromListItem(book)"
                :format="getFormat(book.productId)"
                :initial-quantity="book.quantity || 1"
                :is-owned="!!checkInBookshelf('/books/'+book.productId)"
                :disabled="bookshelfLoading"
              />
            </div>

            <v-btn
              v-if="editingList && canMutateList(data)"
              fab
              small
              class="remove-button"
              :loading="itemToRemove == book.id && removeIsLoading"
              @click.stop="removeBookFromList(book.id)"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </book-list-item>
        </v-row>
        <v-alert
          v-if="data?.bookListItems?.length && displayedBookListItems(data).length === 0 && !isLoading"
          type="info"
          outlined
          class="mt-4"
        >
          {{ $t('bookList.noFilteredResults') }}
        </v-alert>
        <empty-book-list v-if="(!data || data?.bookListItems?.length === 0) && !isLoading" />
      </v-container>
    </template>
  </ApolloQuery>
</template>

<script>
import EmptyBookList from '@/components/EmptyBookList';
import BookListQueries from "@/graphql/BookListQueries";
import BookListItem from "@/components/bookList/BookListItem";
import BookListCartQuantityControl from "@/components/bookList/BookListCartQuantityControl.vue";
import SuccessSnackbar from "@/components/SuccessSnackbar";
import AddBookListToCartButton from '@/components/AddBookListToCartButton';
import SortMenuButton from '@/components/SortMenuButton.vue';
import ShareListButtons from "@/components/bookList/ShareListButtons";
import iris from "@/iris";
import gql from "graphql-tag";
import BookshelfMixin from "@/mixins/BookshelfMixin";
import AdminCheckMixin from "@/mixins/AdminCheckMixin";
import EkitabuSchoolSelect from "@/components/inputs/EkitabuSchoolSelect.vue";
import B2bCustomerSelect from "@/components/inputs/B2bCustomerSelect.vue";
import TaxRegionSelect from "@/components/inputs/TaxRegionSelect.vue";
import { buildBookListExportCsv } from "@/utils/bookListCsvExport";
import LocalBookQueries from "@/graphql/LocalBookQueries";

export default {
  metaInfo() {
    return {
      title: this.$t('routes.userBookList'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$t('seo.bookListDescription'),
        },
      ],
    }
  },
  components: {
    EmptyBookList,
    BookListItem,
    BookListCartQuantityControl,
    SuccessSnackbar,
    AddBookListToCartButton,
    SortMenuButton,
    ShareListButtons,
    EkitabuSchoolSelect,
    B2bCustomerSelect,
    TaxRegionSelect,
  },
  mixins: [BookshelfMixin, AdminCheckMixin],
  data() {
    return {
      loadingItemsCount: 6,
      editingList: false,
      snackbar: false,
      orderAscending: true,
      removeIsLoading: false,
      itemToRemove: null,
      localBooksMap: {},
      bookListItems: [],
      editName: "",
      editNotes: "",
      editDiscountPercentage: null,
      editSchoolId: null,
      editB2bCustomerId: null,
      editTaxRateId: null,
      editIsPublic: false,
      savingSettings: false,
      duplicatingList: false,
      pendingQuantities: {},
      quantityTimers: {},
      quantityInFlight: {},
      duplicateDialog: false,
      duplicateSource: null,
      duplicateName: "",
      duplicateCopyClientAssociation: false,
      duplicateCopyPricingSettings: false,
      sortField: 'name',
      selectedCategoryFilters: [],
      exportIsLoading: false,
      addSearchTerm: "",
      addSearchResults: [],
      addSearchLoading: false,
      addingItemId: null,
      addSearchTimer: null,
      useLegacyBookListQuery: false,
    };
  },
  computed: {
    bookListId() {
      return iris.bookList+this.$route.params.id;
    },
    bookListQuery() {
      return this.useLegacyBookListQuery ? this.legacyBookListQuery : gql`
        query getBookList($id: ID!) {
          bookList(id: $id) {
            id
            _id
            customerId
            name
            isPublic
            allowPublicCart
            slug
            notes
            discountPercentage
            forSchool {
              id
              _id
              name
            }
            forB2bCustomer {
              id
              _id
              organizationName
            }
            taxRate {
              id
              _id
              rate
            }
            bookListItems {
              id
              productId
              name
              thumbnailUrl
              basePrice
              baseCurrency {
                id
                _id
              }
              quantity
              vatIncluded
              position
              createdAt
              updatedAt
            }
          }
        }
      `
    },
    legacyBookListQuery() {
      return gql`
        query getBookListLegacy($id: ID!) {
          bookList(id: $id) {
            id
            _id
            customerId
            name
            isPublic
            slug
            notes
            discountPercentage
            forSchool {
              id
              _id
              name
            }
            forB2bCustomer {
              id
              _id
              organizationName
            }
            taxRate {
              id
              _id
              rate
            }
            bookListItems {
              id
              productId
              name
              thumbnailUrl
              basePrice
              baseCurrency {
                id
                _id
              }
              quantity
              vatIncluded
              position
              createdAt
              updatedAt
            }
          }
        }
      `
    },
    isLoggedIn() {
      return this.$auth.isAuthenticated;
    },
    canEditCuration() {
      return this.isAdmin;
    },
    userId() {
      return this.$auth?.user?.sub?.toString();
    },
    bookshelfProductIds() {
      return this.bookListItems.map((item) => `/books/${item.productId}`);
    },
    sortFieldOptions() {
      return [
        { text: this.$t('bookList.sortAlphabetical'), value: 'name' },
        { text: this.$t('bookList.sortDateAdded'), value: 'createdAt' },
        { text: this.$t('bookList.sortDateUpdated'), value: 'updatedAt' },
      ];
    },
    categoryFilterOptions() {
      return [
        { text: this.$t('bookList.childrenYoungAdult'), value: 'childrenYoungAdult' },
        { text: this.$t('bookList.adult'), value: 'adult' },
        { text: this.$t('bookList.poetry'), value: 'poetry' },
        { text: this.$t('bookList.fiction'), value: 'fiction' },
        { text: this.$t('bookList.textbooks'), value: 'textbooks' },
        { text: this.$t('bookList.bestSellers'), value: 'bestSellers' },
      ];
    },
  },
  beforeDestroy() {
    // Flush nothing on teardown; just stop pending debounce timers.
    Object.values(this.quantityTimers).forEach((t) => clearTimeout(t));
  },
  methods: {
    handleBookListQueryError(error) {
      if (error && !this.useLegacyBookListQuery && this.isAllowPublicCartSchemaError(error)) {
        this.$nextTick(() => {
          this.useLegacyBookListQuery = true;
        });
        return true;
      }

      return false;
    },
    isAllowPublicCartSchemaError(error) {
      const messages = [
        error?.message,
        ...(error?.graphQLErrors || []).map((graphQLError) => graphQLError?.message),
      ].filter(Boolean);

      return messages.some((message) => message.includes('allowPublicCart'));
    },
    async mutateBookListSettings(input) {
      try {
        return await this.$apollo.mutate({
          mutation: BookListQueries.updateBookList,
          variables: { input }
        });
      } catch (error) {
        if (!this.isAllowPublicCartSchemaError(error)) {
          throw error;
        }

        this.useLegacyBookListQuery = true;
        return this.$apollo.mutate({
          mutation: BookListQueries.updateBookListLegacy,
          variables: { input }
        });
      }
    },
    updateBookList(data) {
      const bookList = data?.bookList;
      this.bookListItems = bookList?.bookListItems || [];
      this.fetchLocalBooks(this.bookListItems.map((item) => item.productId));
      return bookList;
    },
    toggleEditing(data) {
      this.editingList = !this.editingList;
      if (this.editingList && data) {
        this.editName = data.name || "";
        this.editNotes = data.notes || "";
        this.editDiscountPercentage = data.discountPercentage != null ? data.discountPercentage : null;
        this.editSchoolId = data.forSchool ? data.forSchool._id : null;
        this.editB2bCustomerId = data.forB2bCustomer ? data.forB2bCustomer._id : null;
        this.editTaxRateId = data.taxRate ? data.taxRate.id : null;
        this.editIsPublic = data.isPublic || false;
      }
    },
    totalQuantity(data) {
      if (!data?.bookListItems) return 0;
      return data.bookListItems.reduce((acc, item) => acc + (item.quantity || 1), 0);
    },
    displayedBookListItems(data) {
      if (!data?.bookListItems) return [];

      const items = this.selectedCategoryFilters.length
        ? data.bookListItems.filter((item) => this.matchesSelectedCategories(item))
        : data.bookListItems;

      return [...items].sort((a, b) => this.compareBookListItems(a, b));
    },
    compareBookListItems(a, b) {
      let comparison = 0;
      if (this.sortField === 'createdAt' || this.sortField === 'updatedAt') {
        comparison = this.compareDates(a[this.sortField], b[this.sortField]);
      } else {
        comparison = this.compareStrings(a.name, b.name);
      }

      return this.orderAscending ? comparison : -comparison;
    },
    compareStrings(a, b) {
      return (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
    },
    compareDates(a, b) {
      return new Date(a || 0).getTime() - new Date(b || 0).getTime();
    },
    matchesSelectedCategories(item) {
      const categoryText = this.bookCategoryText(item.productId);
      return this.selectedCategoryFilters.some((filter) => this.matchesCategoryFilter(categoryText, filter));
    },
    matchesCategoryFilter(categoryText, filter) {
      const text = categoryText.toLowerCase();
      const keywords = {
        childrenYoungAdult: ['children', 'young adult', 'juvenile'],
        adult: ['adult'],
        poetry: ['poetry', 'poem'],
        fiction: ['fiction', 'novel'],
        textbooks: ['textbook', 'education', 'educational'],
        bestSellers: ['best seller', 'bestseller'],
      };

      if (filter === 'adult' && (text.includes('young adult') || text.includes('juvenile'))) {
        return false;
      }

      return keywords[filter].some((keyword) => text.includes(keyword));
    },
    bookCategoryText(productId) {
      const subjects = this.localBooksMap[`/local_books/${productId}`]?.subjects?.edges || [];
      return subjects.map((edge) => edge?.node?.heading || '').join(' ');
    },
    openDuplicateDialog(data) {
      if (!data) return;
      this.duplicateSource = data;
      this.duplicateName = this.$t('bookList.copyOf', { name: data.name || "" });
      this.duplicateCopyClientAssociation = false;
      this.duplicateCopyPricingSettings = false;
      this.duplicateDialog = true;
    },
    async submitDuplicateList() {
      if (!this.duplicateSource || !this.duplicateName.trim()) return;

      this.duplicatingList = true;
      try {
        const input = {
          id: this.duplicateSource.id,
          newName: this.duplicateName.trim(),
        };
        if (this.canEditCuration) {
          input.copyClientAssociation = this.duplicateCopyClientAssociation;
          input.copyPricingSettings = this.duplicateCopyPricingSettings;
        }

        const result = await this.$apollo.mutate({
          mutation: BookListQueries.duplicateBookList,
          variables: { input }
        });
        const duplicatedList = result.data?.duplicateBookList?.bookList;
        if (duplicatedList) {
          this.$store.dispatch('snackbar/showSnackbar', {
            text: this.$t('bookList.duplicatedSuccessfully'),
            color: 'success'
          });
          this.duplicateDialog = false;
          this.$router.push({ name: 'userBookList', params: { id: duplicatedList._id } });
        }
      } catch (error) {
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('errors.general'),
          color: 'error'
        });
        console.error("Error duplicating booklist:", error);
      } finally {
        this.duplicatingList = false;
      }
    },
    async saveSettings(data) {
      if (!data) return;
      this.savingSettings = true;
      try {
        const input = {
          id: data.id,
          name: this.editName,
          isPublic: this.editIsPublic,
          notes: this.editNotes || null,
        };

        if (this.canEditCuration) {
          input.forSchool = this.editSchoolId ? `/schools/${this.editSchoolId}` : null;
          input.forB2bCustomer = this.editB2bCustomerId ? `/b2b_customers/${this.editB2bCustomerId}` : null;
          input.discountPercentage = this.editDiscountPercentage != null && this.editDiscountPercentage !== "" ? parseFloat(this.editDiscountPercentage) : null;
          input.taxRate = this.editTaxRateId || null;
        }

        await this.mutateBookListSettings(input);

        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('bookList.settingsSaved'),
          color: 'success'
        });
        this.editingList = false;
      } catch (error) {
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('errors.general'),
          color: 'error'
        });
        console.error("Error saving booklist settings:", error);
      } finally {
        this.savingSettings = false;
      }
    },
    // Optimistic quantity stepper: clicks update the displayed value
    // immediately and accumulate; one debounced mutation sends the final
    // value. On failure the overlay reverts to the server value.
    displayQuantity(book) {
      return this.pendingQuantities[book.id] ?? (book.quantity || 1);
    },
    bumpQuantity(book, delta) {
      const current = this.displayQuantity(book);
      const next = Math.max(1, current + delta);
      if (next === current) return;
      this.$set(this.pendingQuantities, book.id, next);
      clearTimeout(this.quantityTimers[book.id]);
      this.quantityTimers[book.id] = setTimeout(() => this.flushQuantity(book), 450);
    },
    // Flush every debounced quantity edit NOW: the add-to-cart mutation
    // mirrors the server-side list into the cart, so pending edits must be
    // persisted before it fires.
    async flushPendingQuantities(data) {
      Object.values(this.quantityTimers).forEach((timer) => clearTimeout(timer));
      const pendingIds = Object.keys(this.pendingQuantities);
      const items = data?.bookListItems || [];
      await Promise.all(
        pendingIds
          .map((id) => items.find((item) => item.id === id))
          .filter(Boolean)
          .map((book) => this.flushQuantity(book)),
      );
    },
    async flushQuantity(book) {
      // Awaitable in-flight tracking: callers (debounce timer, the
      // before-cart flush) can rely on this promise chain covering both the
      // running mutation and any newer pending value behind it.
      if (this.quantityInFlight[book.id]) {
        await this.quantityInFlight[book.id];
        return this.flushQuantity(book);
      }
      const target = this.pendingQuantities[book.id];
      if (target == null) return;
      if (target === (book.quantity || 1)) {
        this.$delete(this.pendingQuantities, book.id);
        return;
      }
      const work = (async () => {
        try {
          await this.$apollo.mutate({
            mutation: BookListQueries.updateBookListItem,
            variables: {
              input: {
                id: book.id,
                quantity: target,
              }
            }
          });
          // The cache now carries the new quantity; drop the overlay unless
          // the user kept clicking while the mutation was in flight.
          if (this.pendingQuantities[book.id] === target) {
            this.$delete(this.pendingQuantities, book.id);
          }
        } catch (error) {
          this.$delete(this.pendingQuantities, book.id);
          this.$store.dispatch('snackbar/showSnackbar', {
            text: this.$t('errors.general'),
            color: 'error'
          });
          console.error("Error updating item quantity:", error);
        } finally {
          this.$delete(this.quantityInFlight, book.id);
        }
      })();
      this.$set(this.quantityInFlight, book.id, work);
      await work;
      // A newer value arrived while the mutation ran: flush it too.
      if (this.pendingQuantities[book.id] != null && this.pendingQuantities[book.id] !== target) {
        await this.flushQuantity(book);
      }
    },
    canMutateList(data) {
      return this.isLoggedIn && (this.userId === data?.customerId || this.isAdmin);
    },
    // Owner / admin / checker only. The export is a priced quotation, so it is
    // gated exactly like the edit and settings actions. Used by both the export
    // button (template) and the imperative guard below so they cannot drift.
    canManageList(data) {
      return this.isLoggedIn && (this.userId === data?.customerId || this.isAdmin || this.isChecker);
    },
    showCurationSummary(data) {
      return this.canEditCuration && !!data && (
        !!data.forSchool ||
        !!data.forB2bCustomer ||
        !!data.taxRate ||
        data.discountPercentage != null
      );
    },
    showNotesSummary(data) {
      return this.canManageList(data) && !!data?.notes;
    },
    bookListExportLabels() {
      return {
        headers: [
          this.$t('bookInfo.title'),
          this.$t('authorRoles.author'),
          this.$t('bookInfo.publisher'),
          this.$t('bookInfo.isbn'),
          this.$t('bookInfo.format'),
          this.$t('bookList.csvHeaders.category'),
          this.$t('bookList.csvHeaders.linkToBookDetailPage'),
          this.$t('bookList.csvHeaders.listPrice'),
          this.$t('bookList.csvHeaders.finalPrice'),
          this.$t('bookList.csvHeaders.totalSavings'),
          this.$t('bookList.csvHeaders.priceVatInclusive'),
          this.$t('bookList.csvHeaders.priceWithoutVat'),
          this.$t('bookList.vatComponent'),
          this.$t('common.currency'),
          this.$t('bookList.quantity'),
          this.$t('bookList.lineTotal'),
        ],
        generatedDateLabel: this.$t('bookList.csvHeaders.generatedDate'),
        disclaimerLabel: this.$t('bookList.csvHeaders.disclaimer'),
        unavailableLegacyProduct: this.$t('bookList.csvHeaders.unavailableLegacyProduct'),
      };
    },
    async downloadBookListExport(data) {
      if (!data?.bookListItems?.length) return;
      if (!this.canManageList(data)) return;

      this.exportIsLoading = true;
      try {
        await this.fetchLocalBooks(data.bookListItems.map((item) => item.productId));
        const csv = buildBookListExportCsv(data, this.localBooksMap, {
          origin: window.location.origin,
          disclaimer: this.$t('bookList.quotationDisclaimer'),
          labels: this.bookListExportLabels(),
        });
        this.downloadCsv(csv, `${data.name || 'booklist'}-quotation.csv`);
      } catch (error) {
        console.error("Error downloading booklist export:", error);
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('errors.general'),
          color: 'error',
        });
      } finally {
        this.exportIsLoading = false;
      }
    },
    downloadCsv(csv, fileName) {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName.replace(/[^a-z0-9._-]+/gi, '-').toLowerCase();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    // TODO: https://github.com/ekitabu-LLC/shop-web/issues/1372
    // HACK: BookListItem is a legacy caching entity missing vatIncluded and format.
    // We fetch the actual LocalBooks using aliased GraphQL queries to get these fields.
    // This should be removed when BookList is updated to reference LocalBooks directly.
    async fetchLocalBooks(productIds) {
      if (!productIds?.length) return;

      const aliases = productIds.map((id, i) => {
        return `book${i}: localBook(id: "/local_books/${id}") { id title isbn slug price vatIncluded currency { id _id } publisher { id _id name } format { mimeType _id } bookAuthors { sequence author { name } } subjects(first: 20) { edges { node { heading } } } }`;
      }).join('\n');

      const query = gql`query { ${aliases} }`;

      try {
        const result = await this.$apollo.query({ query });
        this.localBooksMap = {};
        Object.values(result.data).forEach(book => {
          if (book) {
            this.localBooksMap[book.id] = book;
          }
        });
      } catch (e) {
        console.error('Failed to fetch local books:', e);
      }
    },
    getVatIncluded(productId) {
      return this.localBooksMap[`/local_books/${productId}`]?.vatIncluded ?? false;
    },
    getFormat(productId) {
      return this.localBooksMap[`/local_books/${productId}`]?.format ?? null;
    },
    cartBookFromListItem(book) {
      const localBook = this.localBooksMap[`/local_books/${book.productId}`] || {};
      return {
        ...localBook,
        id: `/books/${book.productId}`,
        _id: book.productId,
        title: localBook.title || book.name,
        price: Number(localBook.price ?? book.basePrice ?? 0),
        currency: localBook.currency || book.baseCurrency,
        thumbnailUrl: localBook.thumbnailUrl || book.thumbnailUrl,
        format: localBook.format || null,
        vatIncluded: localBook.vatIncluded ?? book.vatIncluded ?? false,
      };
    },
    async removeBookFromList(bookListItemId) {
      // Re-entrancy guard: a v-btn in loading state still receives clicks.
      if (this.removeIsLoading) return;
      this.removeIsLoading = true;
      this.itemToRemove = bookListItemId;
      const result = await this.$apollo.mutate({
        mutation: BookListQueries.deleteBookListItem,
        variables: {
          id: bookListItemId,
        },
        update: (cache) => {
          const data = cache.readQuery({
            query: this.bookListQuery,
            variables: {
              id: this.bookListId,
            }
          })

          cache.modify({
            id: cache.identify(data.bookList),
            fields: {
              bookListItems(existingItems, { readField }) {
                return existingItems.filter((item) => readField('id', item) !== bookListItemId);
              }
            }
          })
        }
      });

      if (result.data) {
        this.snackbar = true;
      }

      this.removeIsLoading = false;
      this.itemToRemove = null;
    },
    queueCatalogSearch() {
      clearTimeout(this.addSearchTimer);
      const term = (this.addSearchTerm || "").trim();
      if (term.length < 2) {
        this.addSearchResults = [];
        this.addSearchLoading = false;
        return;
      }
      this.addSearchLoading = true;
      this.addSearchTimer = setTimeout(() => this.searchCatalog(term), 300);
    },
    async searchCatalog(term) {
      try {
        const result = await this.$apollo.query({
          query: LocalBookQueries.searchLocalBooks,
          variables: { simplesearch: term, itemsPerPage: 10, page: 1 },
          fetchPolicy: "network-only",
        });
        if ((this.addSearchTerm || "").trim() !== term) return;
        this.addSearchResults = result.data?.localBooks?.collection || [];
      } catch (error) {
        this.addSearchResults = [];
        console.error("Error searching catalog:", error);
      } finally {
        if ((this.addSearchTerm || "").trim() === term) {
          this.addSearchLoading = false;
        }
      }
    },
    clearCatalogSearch() {
      clearTimeout(this.addSearchTimer);
      this.addSearchTerm = "";
      this.addSearchResults = [];
      this.addSearchLoading = false;
    },
    isAlreadyInList(productId, data) {
      return (data?.bookListItems || []).some((item) => item.productId === productId);
    },
    async addItemToList(book, data) {
      if (!data || !book?._id || this.isAlreadyInList(book._id, data)) return;
      this.addingItemId = book._id;
      try {
        await this.$apollo.mutate({
          mutation: BookListQueries.addItemToBookList,
          variables: {
            input: {
              itemId: book._id,
              bookListIri: data.id,
              newListName: null,
            },
          },
          refetchQueries: [{ query: this.bookListQuery, variables: { id: this.bookListId } }],
          awaitRefetchQueries: true,
        });
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('bookList.addedToList'),
          color: 'success',
        });
      } catch (error) {
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('errors.general'),
          color: 'error',
        });
        console.error("Error adding item to booklist:", error);
      } finally {
        this.addingItemId = null;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/quote-document.scss';

.remove-button {
  position: absolute;
  top: 0px;
  right: 0px;
}

// Card quantity block: label row on top, cart pill spanning the card below.
.booklist-card-qty {
  width: 100%;
}

.booklist-card-qty__count {
  min-width: 20px;
  text-align: center;
}

// Widen the pill's fixed row width to fill the card.
.booklist-card-qty .booklist-card-qty__control {
  width: 100%;
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .booklist-card-qty .booklist-card-qty__control.booklist-cart-quantity-control--add {
    width: 40px;
    margin-left: auto;
  }
}

// Settings form: section dividers in the document idiom, fields on the
// app's input-label / input-field chrome.
.settings-section-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $book-grey;
  margin: 20px 0 10px;
  padding-top: 14px;
  border-top: 1px dashed $book-grey-lighter;

  &:first-of-type {
    margin-top: 8px;
    padding-top: 0;
    border-top: none;
  }
}

// The shared .input-field chrome pins slot height to 46px; let the notes
// textarea grow.
.input-field--multiline ::v-deep .v-input__slot {
  height: auto;
  min-height: 46px;
}

// Match the 46px field height so the switch baseline aligns with the
// adjacent text field.
.settings-switch-row {
  height: 46px;
}
</style>
